/**
 * RiskEmergencySection
 * Os sete textos de resposta a emergências do perigo, que alimentam o anexo de emergências do PGR.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

import { useAcao } from '../../../authorization/useVeredito';
import { useDadoPermitido } from '../../../authorization/useListaPermitida';
import riskEmergencyProcedureService from '../../../services/riskEmergencyProcedureService';
import {
  CAMPOS_DO_PROCEDIMENTO_DE_EMERGENCIA,
  LIMITE_DO_TEXTO_DE_EMERGENCIA,
  type CampoDoProcedimentoDeEmergencia,
  type ProcedimentoDeEmergencia,
} from '../../../types/riskEmergencyProcedure';

const ROTULOS: Record<CampoDoProcedimentoDeEmergencia, string> = {
  procedimentos: 'Procedimentos de emergência',
  meios: 'Meios de emergência',
  recursos: 'Recursos disponíveis',
  encaminhamento: 'Encaminhamento dos acidentados',
  responsaveis: 'Responsáveis',
  vazamento: 'Vazamento ou derramamento',
  incendio: 'Combate a incêndio',
};

type Textos = Record<CampoDoProcedimentoDeEmergencia, string>;

const TEXTOS_VAZIOS: Textos = {
  procedimentos: '',
  meios: '',
  recursos: '',
  encaminhamento: '',
  responsaveis: '',
  vazamento: '',
  incendio: '',
};

function comoTextos(procedimento: ProcedimentoDeEmergencia): Textos {
  const textos = { ...TEXTOS_VAZIOS };
  for (const campo of CAMPOS_DO_PROCEDIMENTO_DE_EMERGENCIA) {
    textos[campo] = procedimento[campo] ?? '';
  }
  return textos;
}

function comoProcedimento(textos: Textos): ProcedimentoDeEmergencia {
  const procedimento = {} as ProcedimentoDeEmergencia;
  for (const campo of CAMPOS_DO_PROCEDIMENTO_DE_EMERGENCIA) {
    procedimento[campo] = textos[campo].trim() === '' ? null : textos[campo];
  }
  return procedimento;
}

interface RiskEmergencySectionProps {
  riskId: number | string | undefined;
}

/** O que o formulário do risco pergunta à seção ao salvar ou sair. */
export interface RiskEmergencySectionHandle {
  /** Há texto digitado, ainda não gravado, que o usuário tem permissão de gravar. */
  temAlteracaoNaoGravada: () => boolean;
  /** Grava o que está pendente; `false` quando não foi possível (o erro fica na seção). */
  gravarAlteracoes: () => Promise<boolean>;
}

export const RiskEmergencySection = forwardRef<
  RiskEmergencySectionHandle,
  RiskEmergencySectionProps
>(function RiskEmergencySection({ riskId }, ref) {
  const gravar = useAcao('occupational_risk.update', 'Gravar procedimentos de emergência');
  const [textos, setTextos] = useState<Textos>(TEXTOS_VAZIOS);
  const ultimoGravado = useRef<Textos>(TEXTOS_VAZIOS);
  const [salvando, setSalvando] = useState(false);
  const [erroAoGravar, setErroAoGravar] = useState<string | null>(null);
  const [gravado, setGravado] = useState(false);

  const carregar = useCallback(
    () => riskEmergencyProcedureService.get(riskId as number | string),
    [riskId]
  );
  const procedimento = useDadoPermitido(
    'occupational_risk.read',
    'Procedimentos de emergência',
    carregar,
    {
      habilitado: riskId !== undefined,
    }
  );

  useEffect(() => {
    if (procedimento.dado) {
      const carregados = comoTextos(procedimento.dado);
      ultimoGravado.current = carregados;
      setTextos(carregados);
    }
  }, [procedimento.dado]);

  const passouDoLimite = CAMPOS_DO_PROCEDIMENTO_DE_EMERGENCIA.some(
    campo => textos[campo].length > LIMITE_DO_TEXTO_DE_EMERGENCIA
  );

  const salvar = async (): Promise<boolean> => {
    if (riskId === undefined) return false;
    setSalvando(true);
    setErroAoGravar(null);
    setGravado(false);
    try {
      const salvo = await riskEmergencyProcedureService.save(riskId, comoProcedimento(textos));
      const gravados = comoTextos(salvo);
      ultimoGravado.current = gravados;
      setTextos(gravados);
      setGravado(true);
      return true;
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      setErroAoGravar(
        apiError?.response?.data?.error || 'Não foi possível gravar os procedimentos de emergência'
      );
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const temAlteracao =
    !gravar.disabled &&
    CAMPOS_DO_PROCEDIMENTO_DE_EMERGENCIA.some(
      campo => textos[campo] !== ultimoGravado.current[campo]
    );

  useImperativeHandle(ref, () => ({
    temAlteracaoNaoGravada: () => temAlteracao,
    gravarAlteracoes: async () => {
      if (!temAlteracao) return true;
      if (passouDoLimite) {
        setErroAoGravar(
          `Algum texto passou do limite de ${LIMITE_DO_TEXTO_DE_EMERGENCIA} caracteres`
        );
        return false;
      }
      return salvar();
    },
  }));

  if (riskId === undefined) {
    return <Alert severity='info'>Disponível depois de salvar o risco</Alert>;
  }

  if (procedimento.carregando) {
    return <CircularProgress size={24} aria-label='Carregando procedimentos de emergência' />;
  }

  if (procedimento.bloqueio) {
    return <Alert severity='warning'>{procedimento.bloqueio}</Alert>;
  }

  const alterar = (campo: CampoDoProcedimentoDeEmergencia, valor: string) => {
    setGravado(false);
    setTextos(anteriores => ({ ...anteriores, [campo]: valor }));
  };

  const vazio = CAMPOS_DO_PROCEDIMENTO_DE_EMERGENCIA.every(campo => textos[campo].trim() === '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='h6'>Resposta a emergências</Typography>
      <Typography variant='body2' color='text.secondary'>
        Vale para toda empresa que tenha este perigo.
      </Typography>

      {procedimento.erro && <Alert severity='error'>{procedimento.erro}</Alert>}
      {vazio && !procedimento.erro && (
        <Alert severity='info'>
          Nenhum procedimento cadastrado: o anexo de emergências sairá com traço neste perigo.
        </Alert>
      )}

      {CAMPOS_DO_PROCEDIMENTO_DE_EMERGENCIA.map(campo => {
        const tamanho = textos[campo].length;
        const estourou = tamanho > LIMITE_DO_TEXTO_DE_EMERGENCIA;
        return (
          <TextField
            key={campo}
            label={ROTULOS[campo]}
            value={textos[campo]}
            onChange={evento => alterar(campo, evento.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={gravar.disabled}
            error={estourou}
            helperText={
              estourou
                ? `${tamanho}/${LIMITE_DO_TEXTO_DE_EMERGENCIA} — passou do limite de ${LIMITE_DO_TEXTO_DE_EMERGENCIA} caracteres`
                : `${tamanho}/${LIMITE_DO_TEXTO_DE_EMERGENCIA}`
            }
          />
        );
      })}

      {erroAoGravar && <Alert severity='error'>{erroAoGravar}</Alert>}
      {gravado && <Alert severity='success'>Procedimentos de emergência gravados.</Alert>}

      {gravar.disabled ? (
        <Typography variant='body2' color='text.secondary'>
          {gravar.tooltip}
        </Typography>
      ) : (
        <Box>
          <Button
            type='button'
            variant='contained'
            onClick={() => void salvar()}
            disabled={salvando || passouDoLimite}
          >
            Gravar procedimentos
          </Button>
        </Box>
      )}
    </Box>
  );
});

export default RiskEmergencySection;
